import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {Favorite, ArrowUpward, ArrowDownward} from '@material-ui/icons';
import {apiGetFavouriteProducts} from 'constants/Api';
import {IFavouriteProduct} from 'constants/interfaces';

import './FavouriteProductsWidget.scss';

export default function FavouriteProductsWidget(): React.ReactElement {
  const [products, setProducts] = useState<IFavouriteProduct[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    apiGetFavouriteProducts(10)
      .then((res) => {
        if (alive) setProducts(res);
      })
      .catch((err) => {
        console.error('Failed to load favourite products for widget', err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const renderPriceChange = (current: number, previous: number | null) => {
    if (!previous || current === previous) {
      return (
        <Typography className="price-change none">No recent change</Typography>
      );
    }

    const diff = current - previous;
    const isUp = diff > 0;
    const absDiff = Math.abs(diff);

    return (
      <Box className={`price-change ${isUp ? 'up' : 'down'}`}>
        {isUp ? (
          <ArrowUpward fontSize="inherit" />
        ) : (
          <ArrowDownward fontSize="inherit" />
        )}
        {formatCurrency(absDiff)}
      </Box>
    );
  };

  if (loading) {
    return <Skeleton variant="rect" height={360} style={{borderRadius: 8}} />;
  }

  if (!products) {
    return (
      <Box className="favourite-products-widget">
        <Typography className="widget-heading">FAVOURITE PRODUCTS</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load favourite products.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="favourite-products-widget">
      <Typography className="widget-heading">FAVOURITE PRODUCTS</Typography>

      {products.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No favourite products found.
        </Typography>
      ) : (
        <Box className="products-list">
          {products.map((product) => (
            <Box key={product.productId} className="product-card">
              <Box className="left-col">
                <Box className="fav-badge">
                  <Favorite className="fav-icon" />
                  <Typography className="fav-count">
                    {product.favouriteCount}
                  </Typography>
                </Box>
                <Box className="product-info">
                  <Typography className="product-name">
                    {product.productName}
                  </Typography>
                  <Typography className="stock-code">
                    {product.stockCode}
                  </Typography>
                </Box>
              </Box>
              <Box className="right-col">
                <Typography className="current-price">
                  {formatCurrency(product.currentPrice)}
                </Typography>
                {renderPriceChange(product.currentPrice, product.previousPrice)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
